import type { ReactNode } from 'react';

/**
 * Unified pill/badge primitive. Before this existed, ~12 inline pill spans
 * lived across the admin SPA with drifting shapes (`rounded` vs `rounded-full`)
 * and colors (`text-slate-600` vs `text-slate-700` for the same "neutral"
 * meaning). One primitive, five semantic variants, two sizes.
 *
 * Usage:
 *   <Pill variant="success">Active</Pill>
 *   <Pill variant="danger">Banned</Pill>
 *   <Pill variant="neutral" size="sm">{mode}</Pill>
 *
 * Wrappers `StatusPill`, `BannedPill`, and the inline `Bot` / language / role
 * chips all collapse onto this.
 */

export type PillVariant =
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'neutral'
  | 'accent';

export interface PillProps {
  variant?: PillVariant;
  /** `md` is the default row-friendly size; `sm` for dense tables. */
  size?: 'sm' | 'md';
  children: ReactNode;
  /** Optional testId forwarded as `data-testid`. */
  testId?: string;
  /** Optional arbitrary extras (rare — prefer variant). */
  className?: string;
  /** Optional title attribute for tooltips (e.g. full id on a truncated chip). */
  title?: string;
}

const VARIANT_CLASSES: Record<PillVariant, string> = {
  success: 'bg-emerald-100 text-emerald-800',
  danger: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  info: 'bg-blue-100 text-blue-800',
  neutral: 'bg-slate-100 text-slate-700',
  // `accent` reserves a distinct hue for "bot", "superadmin", or other
  // identity-tagging pills where we don't want to overload success/info.
  accent: 'bg-cyan-100 text-cyan-800',
};

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-xs px-2 py-0.5',
};

export default function Pill({
  variant = 'neutral',
  size = 'md',
  children,
  testId,
  className,
  title,
}: PillProps) {
  return (
    <span
      data-testid={testId}
      title={title}
      className={[
        'inline-flex items-center rounded-full uppercase tracking-wide font-medium',
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </span>
  );
}
